import omit from 'lodash/omit';
import mapValues from 'lodash/mapValues';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import toNumber from 'lodash/toNumber';
import includes from 'lodash/includes';
import get from 'lodash/get';
import moment from 'moment';
import capitalize from 'lodash/capitalize';
import replace from 'lodash/replace';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';

export const logoutUrl = `${window.location.protocol}//${window.location.hostname}:8080/flowable-ui/exit`;
export const redirectUrl = `${window.location.protocol}//${window.location.hostname}:3000/flowable-ui/workflow/appTrack`;
export const redirectErrorPage = `${window.location.protocol}//${window.location.hostname}:3005/error`;

export const dateFormat = 'DD/MM/YYYY';

export const isInvestor = key => key === 'Investor_Screen';
export const isBankEvaluation = key => key === 'bank_evaluation';
export const isAuditor = key =>
  key === 'Auditor_screen_1' || key === 'auditor_screen_2';
export const isUserAbleToAccessReports = groups => !isEmpty(groups) && groups.some(group => includes(group.id, 'G301_GT'));
export const isFinalReview = key => key === 'bank_evaluation_final';
export const isSubmitter = groups =>
  !isEmpty(groups) && groups.some(group => includes(group.id, 'SUBMITTER'));
export const isEditor = groups =>
  !isEmpty(groups) && groups.some(group => includes(group.id, 'EDITOR'));
export const isUserAbleToSubmit = (formKey, groups) =>
  isInvestor(formKey) ||
  (!isInvestor(formKey) &&
    (isSubmitter(groups) || !(isEditor(groups) && !isSubmitter(groups))));

export const outcomes = {
  saveInvestor: 'Αποθήκευση',
  submitInvestor: 'Υποβολή',
  submitBankEvaluation: 'Έγκριση Αίτησης',
  submitBankEvaluationNew: 'Ολοκλήρωση Αξιολόγησης',
  submitBankEvaluationNewToEnd: 'Ολοκλήρωση Ελέγχου',
  rejectBank: 'Aπόρριψη Αίτησης',
  rejectAuditor: 'Απόρριψη Αίτησης',
  returnToInvestor: 'Επιστροφή στο Φορέα Επένδυσης',
  returnToInvestorFromAuditor: 'Επιστροφή στο Φορέα της Επένδυσης',
  returnToBankFromAuditor: 'Επιστροφή στο ΠΙ',
  returnToAuditor: 'Επιστροφή στον ΑΑ',
  returnToBank: 'Επιστροφή στο Πιστωτικό Ίδρυμα',
  returnToAuditorFull: 'Επιστροφή στον Ανεξάρτητο Αξιολογητή',
};

export const handleSave = ({
  state,
  dispatch,
  saveTask,
  taskId,
  formId,
  projectPlanInfo = {},
}) => {
  const carrier = { ...state.carrier };
  const advisor = { ...state.advisor };
  const bank = { ...state.bank };
  const documents = { ...state.documents };
  const loan = { ...state.loan };
  const loanReview = { ...state.loanReview };
  const conflicts = { ...state.conflicts };
  const restDetails = { ...state.restDetails };
  const businessPlanDetails = { ...state.businessPlanDetails };
  const temporaryFramework = { ...state.temporaryFramework };
  const payload = {
    values: {
      ...advisor,
      ...bank,
      ...documents,
      ...carrier,
      ...loan,
      ...loanReview,
      ...conflicts,
      ...restDetails,
      ...businessPlanDetails,
      ...projectPlanInfo,
      ...temporaryFramework,
    },
    formId,
    taskId,
  };
  dispatch(saveTask(payload));
};

export const handleSubmit = ({
  state,
  dispatch,
  submitTask,
  submitTaskNew,
  rejectTask,
  taskId,
  formId,
  formKey,
  outcome,
  banks,
}) => {
  const carrier = { ...state.carrier };
  const advisor = { ...state.advisor };
  const bank = { ...state.bank };
  const documents = { ...state.documents };
  const loan = { ...state.loan };
  const loanReview = { ...state.loanReview };
  const conflicts = { ...state.conflicts };
  const reject = { ...state.reject };
  const returnState = { ...state.return };
  const businessPlanDetails = { ...state.businessPlanDetails };
  const projectPlanInfo = { ...state.projectPlanInfo };
  const temporaryFramework = { ...state.temporaryFramework};
                             

  // Document payload should be string of id for single items (e.g. ibaStockRev: '32432fdw34') and string of csv ids for multiple items (e.g. ibaStockRev: '83293few2342,3943fe234,2131hfe3')
  const parsedDocs = isAuditor(formKey)
    ? {
        lrcAudRep: get(documents, 'lrcAudRep', null)
          ? get(documents, 'lrcAudRep', {}).id
          : null,
        agdAdvContr: get(conflicts, 'agdAdvContr', null)
          ? get(conflicts, 'agdAdvContr', {}).id
          : null,
        lrcAudDocs: get(documents, 'lrcAudDocs', null)
          ? toString(documents.lrcAudDocs.map(doc => doc.id))
          : null,
      }
    : isFinalReview(formKey)
    ? {
        rlcLoanDocs: get(documents, 'rlcLoanDocs', null)
          ? get(documents, 'rlcLoanDocs', {}).id
          : null,
      }
    : mapValues(
        documents,
        doc =>
          isArray(doc) // case of multiple documents (array of objects)
            ? toString(doc.map(d => d.id))
            : !isNil(doc) && isString(doc) // case of radio buttons inside document form
            ? doc
            : !isNil(doc)
            ? doc.id
            : '' // case of single documents (object)
      );

  // Find selected bank and get they key instead of full name.
  const selectedBank = banks.find(b => b.name === bank.bnkBank);
  const bankPayload = {
    ...bank,
    bnkBank: get(selectedBank, 'key', bank.bnkBank),
  };

  let payload = {
    formId,
    taskId,
    outcome,
    values: {
      ...advisor,
      ...bankPayload,
      ...omit(carrier, ['table1', 'table2', 'table3', 'table4', 'table5']),
      ...loan,
      ...loanReview,
      ...conflicts,
      ...parsedDocs,
      ...omit(businessPlanDetails, [
        'deminimisTable',
        'gakCumulationTable',
        'loanCoversTable',
        'pillarEExtroversionTable3',
        'pillarEExtroversionTable2',
        'pillarEExtroversionTable1',
        'pillarDMarketGrowthTable',
        'pillarCInnovationsTable',
        'pillarBDigitalizationsTable',
        'pillarAGreenTransitionsTable',
        'selectableExpensesTable',
        'showEPillarTable3',
        'showEPillarTable2',
        'showEPillarTable1',
        'showDPillar',
        'showCPillar',
        'showBPillar',
        'showAPillar',
        'gakExpenses',
        'investmentPlanBudgets',
        'expenseSelectabilityCheck',
      ]),
      ...projectPlanInfo,
      ...temporaryFramework,
    },
  };

  if (isInvestor(formKey)) {
    payload = {
      ...payload,
      values: {
        ...payload.values,
        genSubSource: 'new',
      },
    };
  }

  if (outcome === outcomes.rejectBank || outcome === outcomes.rejectAuditor) {
    // Case bank/auditor rejects task
    payload = {
      ...payload,
      reject: {
        reason: reject.reason,
        message: reject.message,
        nextProcessStatusCode: isAuditor(formKey)
          ? 'AS_3B'
          : isFinalReview(formKey)
          ? 'AS_4B'
          : 'AS_2B',
      },
    };

    dispatch(rejectTask(payload));
  } else if (
    outcome === outcomes.returnToInvestor ||
    outcome === outcomes.returnToBank ||
    outcome === outcomes.returnToAuditor ||
    outcome === outcomes.returnToBankFromAuditor ||
    outcome === outcomes.returnToInvestorFromAuditor
  ) {
    // Case bank/auditor return task to previous status
    payload = {
      ...payload,
      message: returnState.message,
      reason: 'Επιστροφή',
      nextProcessStatusCode: isBankEvaluation(formKey)
        ? 'AS_2C1'
        : isAuditor(formKey)
        ? outcome === outcomes.returnToInvestorFromAuditor
          ? 'AS_3C1'
          : 'AS_3C2'
        : outcome === outcomes.returnToInvestor
        ? 'AS_4C1'
        : 'AS_4C3',
    };
    dispatch(rejectTask(payload));
  } else {
    dispatch(submitTask(payload));
  }
};

export const handleSubmitNew = ({
  state,
  dispatch,
  submitTaskNew,
  rejectTask,
  taskId,
  formId,
  formKey,
  outcome,
  banks,
}) => {

  dispatch(submitTaskNew(taskId));
}


export const handleReturn = ({
  state,
  dispatch,
  rejectTask,
  taskId,
  formId,
  formKey,
  outcome,
  banks,
}) => {
  handleSubmit({
    state,
    dispatch,
    rejectTask,
    taskId,
    formId,
    formKey,
    outcome,
    banks,
  });
};

// Required fields for Documents
export const isDocumentValid = (document, formKey, state) => {
  if (isAuditor(formKey)) {
    if (document.lrcAudRep) {
      return true;
    }
  } else if (document.ibaBusXls && document.ibaBusPlan && document.ibaBeneficiaries) {
    if(state.documents.ibaSeListed === 'Όχι' && document.ibaCentralRegistry){
      return true;
    } else if(state.documents.ibaSeListed === 'Ναι' && document.ibaNotifArchive){
      return true;
    } else {
      return false;
    }
  }
  return false;
};

// Required fields for Carrier
export const isCarrierValid = carrier => {
  if (
    (sizeValid(carrier?.abiGcrCode, 12) || carrier?.abiGcrCode === '') &&
    carrier?.companyName &&
    validAFM(carrier?.abiVat) &&
    carrier?.companyLegalType &&
    carrier?.companySize &&
    carrier?.town &&
    carrier?.street &&
    carrier?.tk &&
    carrier?.InvMob &&
    carrier?.InvMail
  ) {
    if (!isEmpty(carrier.shareHolders) && !isEmpty(carrier.beneficiariesHolders)) {
      const validShareHolders = carrier.shareHolders.filter(
        shareholder =>
          // validAFM(shareholder.afm) &&
          percentageValid(shareholder.percentage) && shareholder.shareHolderName
      );

      const validBeneficiariesHolders = carrier.beneficiariesHolders.filter(
        beneficiariesHolders =>
          beneficiariesHolders.beneficiaryName &&
          beneficiariesHolders.beneficiaryVat &&
          beneficiariesHolders.beneficiaryBirthDate && 
          percentageValid(beneficiariesHolders.beneficiaryPercentage) && 
          beneficiariesHolders.beneficiaryProperty
      );
      //valid if shareHolders not empty and if valid filtered shareholders length equals with inital shareholders array
      if (validShareHolders && validBeneficiariesHolders &&
          validShareHolders.length !== 0 && validBeneficiariesHolders.length !== 0 && 
          validShareHolders.length === carrier.shareHolders.length && 
          validBeneficiariesHolders.length === carrier.beneficiariesHolders.length
        ) {
          return true;
      } else {
        return false;
      }
    }
    return false;
  }
  return false;
};

// Required fields for Bank
export const isBankValid = (bank, formKey, items) => {
  const banksLength = bank.bnkClbBanks? bank.bnkClbBanks.length : 0;
  // if validBnkClbBanks returns undefined this means that all bnkClbBank are selected and have synBankShare !== 0
  // else if returns an object this means that there is at least one bnkClbBank with null value
  // for bnkClbBank or synBankShare, so submit cannot continue
  const validBnkClbBanks = bank.bnkClbBanks ? bank.bnkClbBanks.find(el => el.bnkClbBank === null || el.synBankShare === null || el.synBankShare === 0) :  null
  const total =
    bank.hasOwnProperty('bnkSyndLoan') && bank.bnkSyndLoan === 'Ναι'
      ? // on some occasions, even when the bank percentages do not have mantissa, their sum is 0.99999999 which does
        // not pass validation, so we do rounding on the 4th digit
        //Math.round(totalCount(bank.bnkClbBanks) * 10000) / 10000
        totalCount(bank.bnkClbBanks)
      : 1;

  const bankValue = bank?.bnkBank && items?.jvBanks
      ? items.jvBanks.find(b => b.key === bank.bnkBank || b.id === bank.bnkBank)
      : null;

  const exists = bankValue && bank.bnkClbBanks ? bank.bnkClbBanks.some(i => i.bnkClbBank === bankValue.name) : false
  
  if (formKey && bank.bnkSyndLoan === 'Ναι' && total === 1 && banksLength !== 1 && validBnkClbBanks === undefined && exists){
      return true
    } 
  if (formKey && total === 1 && (validBnkClbBanks === null || bank.bnkSyndLoan === 'Οχι')){ 
    return true
  };
  if (bank?.bnkBank && !formKey) {
    return true;
  }
  return false;
};

// Required fields for Advisor
export const isAdvisorValid = advisor => {
  if (
    advisor?.advAssisted === 'Όχι' ||
    (advisor?.advAssisted === 'Ναι' &&
      validAFM(advisor?.advVat) &&
      sizeValid(advisor?.advName, 45))
  ) {
    return true;
  }
  return false;
};

// Required fields for Buisiness Plan
export const isBusinessPlanValid = businessPlan => {
  if (
    businessPlan?.abiBpTitle &&
    businessPlan?.abiBpBac &&
    businessPlan?.abiShortDesc &&
    businessPlan?.district &&
    businessPlan?.districtRegion &&
    businessPlan?.municipality &&
    businessPlan?.abiInvLoc
  ) {
    return true;
  }
  return false;
};

// Required fields for Loan
export const isLoanValid = (loan, formKey, documents) => {
  if (isFinalReview(formKey)) {
    if ((sizeValid(loan?.ibaContractNum, 150) && loan?.ibaContractDate )) {
      return true;
    }
  } else if (
    valueValid(loan?.lpaLnAmnt, 999999999) &&
    percentageValid(loan?.lpaLnInter) &&
    loan?.lpaLnIntPer &&    
    valueValid(loan?.lpaLnGrcPer, 60) &&
    loan?.lpaUsrCrRat &&
    loan?.lpaCollat &&
    loan?.loanDuration
  ) {
    return true;
  }
  return false;
};

export const isLoanValidPlaisio = (loan, formKey, documents, Plaisio) => {
  if (isFinalReview(formKey)) {
    if ((sizeValid(loan?.ibaContractNum, 150) && loan?.ibaContractDate )|| documents.rlcLoanDocs) {
      return true;
    }
  } 
    if (Plaisio === "Προσωρινό Πλαίσιο (ΦΕΚ 72642 ΕΞ 2022)"){
      if (
        valueValid(loan?.lpaLnAmnt, 999999999) &&    
        valueValid(loan?.lpaLnGrcPer, 60) &&
        loan?.lpaUsrCrRat &&
        loan?.lpaCollat &&
        loan?.loanDuration
      ) {
        return true;
      }
    }
  return false;
};

// Required fields for Loan Review
export const isLoanReviewValid = (loanReview, Plaisio) => {
  if ( Plaisio === "Προσωρινό Πλαίσιο (ΦΕΚ 72642 ΕΞ 2022)"){
    if (valueValid(loanReview?.rlcLnAmnt, 999999999) &&
    loanReview?.lrcAudCkl1 &&
    loanReview?.lrcAudCkl2 &&
    loanReview?.lrcAudCkl3 &&
    loanReview?.lrcAudCkl4 &&
    loanReview?.lrcAudCkl5 &&
    loanReview?.lrcAudCkl6 &&
    valueValid(loanReview?.eisRrfLnAmnt, 999999999)
    )
    {
      return true;
    }
    } else {
      if (
        valueValid(loanReview?.rlcLnAmnt, 999999999) &&
        loanReview?.lrcAudCkl1 &&
        loanReview?.lrcAudCkl2 &&
        loanReview?.lrcAudCkl3 &&
        loanReview?.lrcAudCkl4 &&
        loanReview?.lrcAudCkl5 &&
        loanReview?.lrcAudCkl6 &&
        valueValid(loanReview?.eisRrfLnAmnt, 999999999) &&
        percentageValid(loanReview?.rlcLnInter) &&
        (loanReview?.rlcLnInter > 0 )
      ){
        return true;
      }
  }   
  return false;
}; 

// Required fields for Conflicts
export const isConflictsValid = conflicts => {
  if (conflicts?.agdAdvContDt) {
    return true;
  }
  return false;
};

export const isFinalReviewDocumentsValid = documents => {


}

export const handleSubmitDisabled = ({ state, formKey }) => {
  const {
    conflicts,
    loan,
    bank,
    advisor,
    businessPlanDetails,
    carrier,
    loanReview,
    documents,
    formValidity,
    items,
  } = state;

  if (
    isInvestor(formKey) &&
    validated(formValidity) &&
    isAdvisorValid(advisor) &&
    isBankValid(bank) &&
    isBusinessPlanValid(businessPlanDetails) &&
    isCarrierValid(carrier) &&
    isDocumentValid(documents, formKey, state)
  ) {
    return false;
  }
  if (
    isBankEvaluation(formKey) &&
    validated(formValidity) &&
    isAdvisorValid(advisor) &&
    isBankValid(bank, formKey, items) &&
    isBusinessPlanValid(businessPlanDetails) &&
    isCarrierValid(carrier) &&
    isLoanValid(loan, formKey) 
  ) {
    return false;
  }
  if (
    isAuditor(formKey) &&
     validated(formValidity) &&
     isAdvisorValid(advisor) &&
     isBankValid(bank, formKey, items) &&
     isBusinessPlanValid(businessPlanDetails) &&
    isCarrierValid(carrier) &&
    isConflictsValid(conflicts) &&
     isLoanValid(loan, formKey) &&
    isDocumentValid(documents, formKey) &&
    isLoanReviewValid(loanReview,businessPlanDetails.frameOfGovernmentFunding)
  ) {
    return false;
  }
  if (
    isFinalReview(formKey) &&
    validated(formValidity) &&
    isAdvisorValid(advisor) &&
    isBankValid(bank) &&
    isBusinessPlanValid(businessPlanDetails) &&
    isCarrierValid(carrier) &&
    isLoanValid(loan, formKey, documents) &&
    isLoanReviewValid(loanReview,businessPlanDetails.frameOfGovernmentFunding)
  ) {
    return false;
  }
  return true;
};

export const handleCheckUniqueness = async ({
  state,
  dispatch,
  taskId,
  formId,
  banks,
  checkUniqueness,
}) => {
  const carrier = { ...state.carrier };
  const advisor = { ...state.advisor };
  const bank = { ...state.bank };
  const documents = { ...state.documents };

  const parsedDocs = mapValues(
    documents,
    doc =>
      isArray(doc) // case of multiple documents (array of objects)
        ? toString(doc.map(d => d.id))
        : !isNil(doc) && isString(doc) // case of radio buttons inside document form
        ? doc
        : !isNil(doc)
        ? doc.id
        : '' // case of single documents (object)
  );

  // Find selected bank and get they key instead of full name.
  const selectedBank = banks.find(b => b.name === bank.bnkBank);
  const bankPayload = {
    ...bank,
    bnkBank: get(selectedBank, 'key', bank.bnkBank),
  };

  const payload = {
    taskId,
    formId,
    values: {
      ...advisor,
      ...bankPayload,
      ...omit(carrier, ['table1', 'table2', 'table3', 'table4', 'table5']),
      ...parsedDocs,
    },
  };
  const response = await dispatch(checkUniqueness(payload));
  return response;
};

export const validAFM = afm => {
  if (afm && afm.length !== 9) {
    // "Τα ψηφία του ΑΦΜ πρέπει να είναι 9 αριθμοί"
    return false;
  }

  if (!/^\d+$/.test(afm)) {
    // "Αυτό που εισάγετε δεν είναι αριθμός"
    return false;
  }

  if (afm === '0'.repeat(9)) {
    // "Αυτό που εισάγετε είναι μηδενικός αριθμός (000000000)"
    return true;
  }

  const sum = afm
    .substring(0, 8)
    .split('')
    .reduce((s, v, i) => s + (parseInt(v) << (8 - i)), 0);

  const calc = sum % 11;
  const d9 = parseInt(afm[8]);
  const valid = calc % 10 === d9;

  return valid;
};

export const sizeValid = (field, rule) => {
  if (field && field.length <= rule) {
    return true;
  }
  return false;
};

export const valueValid = (field, rule) => {
  if (field >= 0 && field <= rule) {
    return true;
  }
  return false;
};

export const percentageValid = field => {
  if (isNil(field)) {
    return false;
  }
  if (field >= -1 && field <= 1) {
    return true;
  }
  return false;
};

export const validated = formValidity => {
  if (formValidity) {
    return Object.keys(formValidity).filter(
      key => !(formValidity[key] === false)
    );
  }
  return true;
};

export const handleProjectPlanInfo = async ({
  state,
  dispatch,
  getProjectPlanInfo,
}) => {
  const businessPlanDetails = { ...state.businessPlanDetails };
  const restDetails = { ...state.restDetails };

  const response = await dispatch(
    getProjectPlanInfo(
      businessPlanDetails.abiVat,
      get(restDetails, 'investmentPlanElements.addedSchema', '')
    )
  );
  return response;
};

export const validateAFM = afm => {
  if (afm.length !== 9) {
    // "Τα ψηφία του ΑΦΜ πρέπει να είναι 9 αριθμοί"
    return Promise.reject('Τα ψηφία του ΑΦΜ πρέπει να είναι 9 αριθμοί');
  }

  if (!/^\d+$/.test(afm)) {
    // "Αυτό που εισάγετε δεν είναι αριθμός"
    return Promise.reject('Αυτό που εισάγετε δεν είναι αριθμός');
  }

  if (afm === '0'.repeat(9)) {
    // "Αυτό που εισάγετε είναι μηδενικός αριθμός (000000000)"
    //return Promise.reject('Αυτό που εισάγετε δεν είναι έγκυρο ΑΦΜ');
    return Promise.resolve();
  }
  const sum = afm
    .substring(0, 8)
    .split('')
    .reduce((s, v, i) => s + (parseInt(v) << (8 - i)), 0);

  const calc = sum % 11;
  const d9 = parseInt(afm[8]);
  const valid = calc % 10 === d9;

  return valid
    ? Promise.resolve()
    : Promise.reject('Αυτό που εισάγετε δεν είναι έγκυρο ΑΦΜ');
};

//todo test validator
export const validateEmail = email => {
  var mailformat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  if (email != null && email.match(mailformat)) {
    return true;
  } else {
    return false;
  }
};

export const formattedNumber = (value = 0) => {
  let parts = value.toString().split('.');
  // this check is enchanced for showing 2 decimals when the input has only one decimal. (e.g 5.7 will be converted to 5.70)
  // in case of multiple digits int decimal part of number round only the first two
  parts[1] =
    parts.length === 1
      ? '00'
      : parts[1] !== '' && parts[1][0] === '0'
      ? '0' +
        Math.round(
          parseFloat(parts[1]) / 10 ** (parts[1].length - 2)
        ).toString()
      : Math.round(
          parseFloat(parts[1]) / 10 ** (parts[1].length - 2)
        ).toString();
  
  parts[0] = Number(parts[1]) === 100 ? Number(parts[0])+1 : Number(parts[0])
  parts[0] = parts[0] ? Number(parts[0]).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '0';
  parts[1] = Number(parts[1]) === 100 ? '00' : parts[1]
  
  return parts.join(',');
};

export const formattedPercentage = (value = 0) => {
  return formattedNumber(value * 100);
};

export const formattedValuesForShareHolders = values => {
  var formValues = cloneDeep(values);

  formValues.shareHolders &&
    formValues.shareHolders.map(
      item => (item.percentage = formattedPercentage(item.percentage))
    );

  return formValues;
};

export const filterGakPaidCategory = (value = '') => {
  if (value === 'Σύνολο') {
    return 'Σύνολο';
  }

  let info =
    !isEmpty(value) && value !== 'Μη ενισχυόμενη δαπάνη'
      ? exportGakCategory(value)
      : 'Μη αντιστοίχιση με ΓΑΚ';
  return info;
};

export const exportGakCategory = (value = '') => {
  //receives gak category and return trimmed string between parentheses, replacing certain words with '.'
  let trimmed = replace(value, '[', '(');
  trimmed = trimmed.substring(trimmed.indexOf('(') + 1, trimmed.indexOf(')'));
  trimmed = replace(trimmed, / παράγραφος | στοιχείο /g, '.');
  return capitalize(trimmed);
};

export const numberInputFormatter = value => {
  value = toFixedWithoutRounding(value);
  if (value) return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return;
};

export const numberInputParser = value => {
  if (value) return value.replace(/\./g, '').replace(',', '.');
  return;
};

export const toFixedWithoutRounding = number => {
  // receives a float number and keeps only 2 decimals.
  if (number) {
    // notice: the function has been modified to use round() and not floor()
    const value = (Math.round(toNumber(number) * 100) / 100).toFixed(2);
    const parsed = value.split('.');
    // replaced dot with comma
    return parsed[1] === '00' ? parsed[0] : value.replace('.', ',');
  }
  return;
};

export const hasReturnedToInvestor = status => {
  if (
    status === 'AS_2C1' || // Επιστροφή στο Φορέα Επένδυσης
    status === 'AS_3C1' || // Επιστροφή στο Φορέα της Επένδυσης
    status === 'AS_4C1' // Επιστροφή στο Φορέα Επένδυσης
  ) {
    return true;
  }
  return false;
};

export const hasReturnedToInvestorThreeOrFour = status => {
  if (
    status === 'AS_3C1' || // Επιστροφή στο Φορέα της Επένδυσης
    status === 'AS_4C1' // Επιστροφή στο Φορέα Επένδυσης
  ) {
    return true;
  }
  return false;
};

export const hasReturnedToInvestorTwoOrFour = status => {
  if (
    status === 'AS_3C1'  // Επιστροφή στο Φορέα της Επένδυσης
    
  ) {
    return true;
  }
  return false;
};


export const isNotInvestor = formKey => {
  if (isInvestor(formKey) 
  ) {
    return false;
  }
  return true;
};

export const validateDateCompletion = value => {
  const formattedValue = isString(value) ? moment(value) : value;
  let valid = true;

  if (formattedValue.isAfter('2027-01-01')) {
    valid = false;
  }

  return valid
    ? Promise.resolve()
    : Promise.reject(
        'Η εκτιμώμενη ημερομηνία ολοκλήρωσης του ΕΣ χρειάζεται να ειναι έως το 2026'
      );
};

export const getFullDateHours = date => {
  if (date && moment(date).isValid()) {
    return moment(date).format('DD/MM/YYYY HH:mm:ss');
  }
};

export const setLocalStorageErrors = (error, text) => {
  const item = [{
    date: moment(new Date()).format('DD/MM/YYYY'),
    messages: [
      {
        created: new Date(),
        comment: text,
        description: error.response.data.message,
        flag: 'red',
      },
    ],
  }];

  localStorage.setItem('items', JSON.stringify(item))
}

export const totalCount = (elem) => elem?.reduce(
  (prev, bank) => prev + bank.synBankShare,
  0
);